import { styled, css } from "styled-components";
import { mobileBreakPointCSS } from "../../defaults/theme.js";
import { cssFontSize, cssSpacing, surfaceColorMap } from "../../util/styles.js";
import { Dots as CarouselDots } from "../../hooks/useCarousel.js";
import type { BackgroundColor } from "../../types/theme.js";

export const Background = styled.div<{ $bg?: BackgroundColor }>`
  ${(p) =>
    p.$bg
      ? css`
          background-color: ${surfaceColorMap[p.$bg]};
          color: ${p.$bg === "surface3" ? "#fff" : "var(--swatch2)"};
          svg path { fill: currentColor; stroke: currentColor; }
          && h1, && p { color: inherit; }
        `
      : css`
          background-color: var(--hero-background);
        `}
  width: 100%;
  overflow: hidden;

  @media (min-width: 1100px) {
    border-radius: ${(props) => props.theme.borderRadius};
  }
`;

export const HorizontalSpacer = styled.div`
  padding: ${cssSpacing("l")} ${cssSpacing("s")};

  @media (min-width: ${mobileBreakPointCSS}) {
    padding: ${cssSpacing("xl")} ${cssSpacing("l")};
  }
`;

export const Layout = styled.div<{ $hasImage?: boolean; $hasSlides: boolean }>`
  display: flex;
  flex-direction: column;
`;

export const Content = styled.div<{ $hasImage?: boolean; $hasSlides: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${cssSpacing("s")};

  p {
    color: var(--hero-description-color, inherit);
    font-size: ${cssFontSize("small")};

    @media (min-width: ${mobileBreakPointCSS}) {
      font-size: ${cssFontSize("medium")};
    }
  }
`;

export const Title = styled.h1<{ $size?: number }>`
  margin: 0;
  color: var(--hero-title-color, inherit);
  font-size: ${(p) => p.$size ? `${p.$size}px` : p.theme.fontSizes.larger};

  @media (min-width: ${mobileBreakPointCSS}) {
    font-size: ${(p) => p.$size ? `${p.$size}px` : p.theme.fontSizes.largest};
  }
`;

export const Buttons = styled.div<{ $hasImage?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${cssSpacing("s")};
  margin-top: ${cssSpacing("s")};

  @media (min-width: ${mobileBreakPointCSS}) {
    flex-direction: row;
  }
`;

export const HeroImageWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: flex-end;
  min-height: 600px;
  overflow: hidden;

  @media (min-width: 1100px) {
    border-radius: ${(props) => props.theme.borderRadius};
  }
`;

export const ContentPanel = styled.div<{ $bg?: BackgroundColor }>`
  ${(p) =>
    p.$bg
      ? css`
          background-color: ${surfaceColorMap[p.$bg]};
          color: ${p.$bg === "surface3" ? "#fff" : "var(--swatch2)"};
          svg path { fill: currentColor; stroke: currentColor; }
          && h1, && p { color: inherit; }
        `
      : css`
          background-color: var(--hero-graphic-card-color);
        `}
  position: relative;
  z-index: 1;
  padding: ${cssSpacing("s")};
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: ${cssSpacing("s")};
  width: 100%;
  max-width: 600px;
  border-top-right-radius: ${(props) => props.theme.borderRadius};

  @media (min-width: ${mobileBreakPointCSS}) {
    padding: ${cssSpacing("l")};
  }

  h1 {
    margin: 0;
    color: var(--hero-graphic-title-color, inherit);
    font-size: ${cssFontSize("larger")};

    @media (min-width: ${mobileBreakPointCSS}) {
      font-size: ${cssFontSize("largest")};
    }
  }

  p {
    margin: 0;
    color: var(--hero-graphic-description-color, inherit);
    font-size: ${cssFontSize("small")};

    @media (min-width: ${mobileBreakPointCSS}) {
      font-size: ${cssFontSize("medium")};
    }
  }
`;

export const HeroImg = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

export const VideoWrapper = styled.div<{ $hasContent?: boolean }>`
  position: relative;
  min-height: 600px;
  display: flex;
  align-items: flex-end;
  overflow: hidden;

  @media (min-width: 1100px) {
    border-radius: ${(props) => props.theme.borderRadius};
  }

  video {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  ${(props) =>
    props.$hasContent &&
    `&::after {
      content: "";
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.4);
    }`}
`;

export const VideoContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 600px;
  color: var(--hero-graphic-title-color, #fff);
`;

export const Slider = styled.div`
  position: relative;
`;

export const SlideWrapper = styled.div`
  display: none;

  &.active {
    display: block;
  }
`;

export const SlideButtons = styled.div`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  display: flex;
  justify-content: space-between;
  width: 100%;
`;

export const SlideButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: ${cssSpacing("s")};

  &.prev svg {
    transform: rotate(180deg);
  }
`;

export const Dots = styled(CarouselDots)`
  margin-top: ${cssSpacing("s")}
`;
